import { SurveillanceSessionGate, sameOriginSurveillance } from "../../ha/SurveillanceSessionGate";
import { useMemo, useState, useEffect, useRef } from 'react';
import {
  Lightbulb, ToggleLeft, Gauge, Blinds, Thermometer, Radio,
  LayoutGrid, Sun, Cloud, Home, Zap, Star, Building2, Layers, Sparkles, List,
  ChevronLeft, ChevronRight, DoorOpen, Camera, ChevronDown, ChevronUp, Maximize2, Minimize2,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useRegistry } from '../../registry/hooks';
import type { Area, Entity, Floor } from '../../registry/types';
import { buildRegistryMaps, isCustomerHidden } from '../../registry/utils';
import { EntityCard, domainForEntityId } from '../cards/EntityCard';
import { EmptyDashboardState } from './EmptyState';
import { useViewMode } from '../../context/ViewModeContext';
import { useFavorites, HA_LABEL_FAV } from '../../hooks/useFavorites';

// ============================================================
// Name/Floor/Building Extraction
// ============================================================

export function cleanAreaName(name: string): string {
  return name
    .replace(/^-?\d+\.\s+/, '')
    .replace(/^U-?\d+\.\s+/i, '')
    .replace(/^G\d+\s+/, '')
    .trim();
}

export function extractBuilding(name: string): string | null {
  const m1 = name.match(/^G(\d+)\s+/);
  if (m1) return `Gebäude ${m1[1]}`;
  const m2 = name.match(/^Gebäude\s+(\d+)/i);
  if (m2) return `Gebäude ${m2[1]}`;
  return null;
}

interface FloorInfo { key: string; label: string; order: number }

export function extractFloorFromName(name: string): FloorInfo {
  const m = name.match(/^(-?\d+|U-?\d+)\./i);
  if (m) {
    const raw = m[1].toLowerCase();
    if (raw === '-1') return { key: 'aussen', label: 'Außen', order: -1 };
    if (raw === '0') return { key: 'eg', label: 'EG', order: 0 };
    if (raw === '1') return { key: 'og1', label: '1. OG', order: 1 };
    if (raw === '2') return { key: 'og2', label: '2. OG', order: 2 };
    if (raw.startsWith('u')) return { key: 'ug', label: 'UG', order: -2 };
    return { key: `f-${raw}`, label: raw.toUpperCase(), order: 10 };
  }
  return { key: 'misc', label: 'Allgemein', order: 99 };
}

// ============================================================
// Types
// ============================================================

interface DomainGroup {
  key: string;
  label: string;
  Icon: typeof Lightbulb;
  entities: Entity[];
}

const DOMAIN_ORDER: Array<{ key: string; label: string; Icon: typeof Lightbulb; domains: string[] }> = [
  { key: 'light', label: 'Licht', Icon: Lightbulb, domains: ['light'] },
  { key: 'switch', label: 'Schalter', Icon: ToggleLeft, domains: ['switch'] },
  { key: 'cover', label: 'Rolläden', Icon: Blinds, domains: ['cover'] },
  { key: 'climate', label: 'Klima', Icon: Thermometer, domains: ['climate'] },
  { key: 'sensor', label: 'Sensoren', Icon: Gauge, domains: ['sensor', 'binary_sensor'] },
  { key: 'action', label: 'Aktionen', Icon: Sparkles, domains: ['button', 'lock'] },
  { key: 'media_player', label: 'Medien', Icon: Radio, domains: ['media_player'] },
];

function groupEntitiesByDomain(entities: Entity[]): DomainGroup[] {
  const groups: DomainGroup[] = [];
  for (const spec of DOMAIN_ORDER) {
    const filtered = entities.filter((e) => spec.domains.includes(domainForEntityId(e.entity_id)));
    if (filtered.length === 0) continue;
    groups.push({ key: spec.key, label: spec.label, Icon: spec.Icon, entities: filtered });
  }
  return groups;
}

interface RoomTab {
  areaId: string;
  areaName: string;
  raw: string;
  entities: Entity[];
  building: string | null;
  floor: FloorInfo;
}

// ============================================================
// Hero-Bar
// ============================================================

const HERO_STORAGE_KEY = 'rsc-dash.hero-expanded';

function HeroBar({ tabs }: { tabs: RoomTab[] }): JSX.Element {
  const totalRooms = tabs.length;
  const totalEntities = tabs.reduce((sum, t) => sum + t.entities.length, 0);
  const onEntities = tabs.reduce((sum, t) => sum + t.entities.filter((e) => e.state === 'on').length, 0);
  const now = new Date();
  const time = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const date = now.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
  const hour = now.getHours();
  const gradient =
    hour < 6 || hour >= 20 ? 'from-indigo-900 via-slate-900 to-slate-950'
    : hour < 10 ? 'from-amber-200 via-orange-300 to-sky-400'
    : hour < 17 ? 'from-sky-400 via-cyan-400 to-blue-500'
    : 'from-orange-400 via-rose-400 to-purple-600';
  const WeatherIcon = hour < 6 || hour >= 20 ? Cloud : Sun;

  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem(HERO_STORAGE_KEY) === '1'; } catch { return false; }
  });
  const toggle = () => {
    setExpanded((v) => {
      const next = !v;
      try { localStorage.setItem(HERO_STORAGE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };
  const ChevronIcon = expanded ? ChevronUp : ChevronDown;

  return (
    <div className={`rsc-hero relative mb-4 overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg`} data-testid="hero-bar" data-expanded={expanded}>
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between gap-4 px-4 py-2 text-left"
        aria-expanded={expanded}
        aria-controls="rsc-hero-panel"
        data-testid="hero-toggle"
      >
        <div className="flex items-baseline gap-3">
          <span className="text-lg font-bold tabular-nums">{time}</span>
          <span className="hidden text-xs opacity-80 sm:inline">{date}</span>
        </div>
        <div className="flex items-center gap-3 text-xs opacity-80">
          <span className="flex items-center gap-1"><Zap className="h-3 w-3" aria-hidden="true" />{onEntities}/{totalEntities}</span>
          <span className="flex items-center gap-1"><Home className="h-3 w-3" aria-hidden="true" />{totalRooms}</span>
          <ChevronIcon className="h-4 w-4" aria-hidden="true" />
        </div>
      </button>
      {expanded && (
        <div id="rsc-hero-panel" className="grid grid-cols-2 gap-4 border-t border-white/20 p-4 md:grid-cols-4">
          <HeroCell label="Zeit" value={time} sub={date} />
          <HeroCell label="Wetter" value="—" sub="Sensor folgt" Icon={WeatherIcon} />
          <HeroCell label="Aktiv" value={`${onEntities} / ${totalEntities}`} sub="Geräte an" Icon={Zap} />
          <HeroCell label="Räume" value={String(totalRooms)} sub="im Haus" Icon={Home} />
        </div>
      )}
    </div>
  );
}

function HeroCell({ label, value, sub, Icon }: { label: string; value: string; sub: string; Icon?: typeof Sun }): JSX.Element {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-80">
        {Icon && <Icon className="h-3 w-3" aria-hidden="true" />}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-xs opacity-80">{sub}</div>
    </div>
  );
}

// ============================================================
// Domain-Section (Card-Grid pro Domain, optional mit Raum-Chip)
// ============================================================

function DomainSection({ group, entityRoomMap }: { group: DomainGroup; entityRoomMap?: Map<string, string> }): JSX.Element {
  const Icon = group.Icon;
  return (
    <section className="rsc-domain-section space-y-2" data-testid={`domain-section-${group.key}`}>
      <div className="flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
        <Icon className="h-3.5 w-3.5 text-[var(--rsc-primary)]" aria-hidden="true" />
        <span>{group.label}</span>
        <span className="text-[10px] font-normal opacity-60">{group.entities.length}</span>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {group.entities.map((entity) => (
          <div key={entity.entity_id} className="relative">
            <EntityCard entityId={entity.entity_id} />
            {entityRoomMap && entityRoomMap.get(entity.entity_id) && (
              <div className="pointer-events-none absolute bottom-1 right-1 z-10 flex items-center gap-1 rounded-full bg-gray-900/70 px-2 py-0.5 text-[10px] font-medium text-white shadow-sm">
                <DoorOpen className="h-2.5 w-2.5" aria-hidden="true" />
                {entityRoomMap.get(entity.entity_id)}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// Room-Detail (nach Drill-Down: eine einzelne Raum-Ansicht)
// ============================================================

function RoomDetail({ tab, onBack, backLabel }: { tab: RoomTab; onBack: () => void; backLabel: string }): JSX.Element {
  const groups = useMemo(() => groupEntitiesByDomain(tab.entities), [tab.entities]);
  const onCount = tab.entities.filter((e) => e.state === 'on').length;

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--rsc-primary)] hover:bg-gray-100 dark:hover:bg-gray-800"
        data-testid="room-back"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" /> {backLabel}
      </button>
      <header className="flex items-center justify-between rounded-xl bg-white/70 p-4 shadow-sm dark:bg-gray-800/60">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{tab.areaName}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {tab.entities.length} Geräte {onCount > 0 && `· ${onCount} an`}
            {tab.building && ` · ${tab.building}`}
            {` · ${tab.floor.label}`}
          </p>
        </div>
        {onCount > 0 && <div className="flex h-3 w-3 rounded-full bg-emerald-500 shadow-[0_0_10px_theme(colors.emerald.500)]" aria-hidden="true" />}
      </header>
      {groups.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Keine Geräte in diesem Raum.</p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (<DomainSection key={group.key} group={group} />))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Räume-View mit Drill-Down (Gebäude → Etage → Raum)
// ============================================================

interface BuildingGroup {
  key: string;
  label: string;
  floors: Array<{ key: string; label: string; order: number; rooms: RoomTab[] }>;
}

function groupByBuildingAndFloor(tabs: RoomTab[]): BuildingGroup[] {
  const buildingMap = new Map<string, Map<string, { key: string; label: string; order: number; rooms: RoomTab[] }>>();
  const NO_BUILDING = '__no_building__';
  for (const tab of tabs) {
    const bKey = tab.building ?? NO_BUILDING;
    if (!buildingMap.has(bKey)) buildingMap.set(bKey, new Map());
    const floors = buildingMap.get(bKey)!;
    const existing = floors.get(tab.floor.key);
    if (existing) existing.rooms.push(tab);
    else floors.set(tab.floor.key, { key: tab.floor.key, label: tab.floor.label, order: tab.floor.order, rooms: [tab] });
  }
  const groups: BuildingGroup[] = [];
  for (const [bKey, floorMap] of buildingMap.entries()) {
    const floors = Array.from(floorMap.values()).sort((a, b) => a.order - b.order);
    groups.push({ key: bKey, label: bKey === NO_BUILDING ? 'Räume' : bKey, floors });
  }
  return groups.sort((a, b) => a.label.localeCompare(b.label));
}

interface RoomsNavState { building?: string; floor?: string; room?: string }

function TileButton({ label, subLabel, count, onCount, onClick, Icon, testId }: {
  label: string; subLabel?: string; count: number; onCount?: number; onClick: () => void; Icon: typeof Building2; testId?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group relative flex flex-col items-start gap-2 rounded-xl border border-gray-200/60 bg-white/50 p-4 text-left transition hover:border-[var(--rsc-primary)] hover:shadow-md dark:border-gray-700/40 dark:bg-gray-800/40 dark:hover:border-[var(--rsc-primary)]"
    >
      <div className="flex w-full items-center justify-between">
        <Icon className="h-6 w-6 text-[var(--rsc-primary)]" aria-hidden="true" />
        <ChevronRight className="h-5 w-5 text-gray-400 opacity-60 transition group-hover:translate-x-1 group-hover:opacity-100" aria-hidden="true" />
      </div>
      <div className="flex-1">
        <div className="font-bold text-gray-900 dark:text-white">{label}</div>
        {subLabel && <div className="text-xs text-gray-500 dark:text-gray-400">{subLabel}</div>}
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span>{count} {count === 1 ? 'Eintrag' : 'Einträge'}</span>
        {onCount !== undefined && onCount > 0 && (
          <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-600 dark:text-emerald-400">
            <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
            {onCount} an
          </span>
        )}
      </div>
    </button>
  );
}

function countOnEntities(rooms: RoomTab[]): number {
  return rooms.reduce((s, r) => s + r.entities.filter((e) => e.state === 'on').length, 0);
}

function RoomsView({ tabs, resetKey }: { tabs: RoomTab[]; resetKey?: number }): JSX.Element {
  const buildings = useMemo(() => groupByBuildingAndFloor(tabs), [tabs]);
  const [nav, setNav] = useState<RoomsNavState>({});
  useEffect(() => { setNav({}); }, [resetKey]);

  // Auto-Skip: wenn nur EIN Gebäude ohne Label, direkt in dessen Ansicht
  const soloBuilding = buildings.length === 1 ? buildings[0] : undefined;
  const effectiveBuilding = nav.building ?? soloBuilding?.key;
  const building = effectiveBuilding ? buildings.find((b) => b.key === effectiveBuilding) : undefined;

  // Auto-Skip: wenn Gebäude nur EINE Etage hat, direkt in Etage
  const soloFloor = building && building.floors.length === 1 ? building.floors[0] : undefined;
  const effectiveFloor = nav.floor ?? soloFloor?.key;
  const floor = building && effectiveFloor ? building.floors.find((f) => f.key === effectiveFloor) : undefined;

  const room = floor && nav.room ? floor.rooms.find((r) => r.areaId === nav.room) : undefined;

  // Level 3: Raum-Detail
  if (room && floor && building) {
    return (
      <RoomDetail
        tab={room}
        backLabel={floor.rooms.length > 1 ? `${floor.label} (${floor.rooms.length} Räume)` : building.label}
        onBack={() => setNav({ building: nav.building, floor: nav.floor })}
      />
    );
  }

  // Level 2: Räume in einer Etage listen
  if (floor && building) {
    const showBack = nav.floor !== undefined || nav.building !== undefined;
    return (
      <div className="space-y-4">
        {showBack && (
          <button
            type="button"
            onClick={() => setNav(nav.floor && building.floors.length > 1 ? { building: nav.building } : {})}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--rsc-primary)] hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid="floor-back"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            {building.floors.length > 1 ? building.label : 'Zurück'}
          </button>
        )}
        <header className="mb-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{floor.label}</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{building.label} · {floor.rooms.length} Räume</p>
        </header>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {floor.rooms.map((r) => (
            <TileButton
              key={r.areaId}
              label={r.areaName}
              subLabel={`${r.entities.length} Geräte`}
              count={r.entities.length}
              onCount={r.entities.filter((e) => e.state === 'on').length}
              onClick={() => setNav({ building: nav.building, floor: nav.floor, room: r.areaId })}
              Icon={DoorOpen}
              testId={`room-tile-${r.areaId}`}
            />
          ))}
        </div>
      </div>
    );
  }

  // Level 1: Etagen in einem Gebäude listen
  if (building) {
    const showBack = nav.building !== undefined && !soloBuilding;
    return (
      <div className="space-y-4">
        {showBack && (
          <button
            type="button"
            onClick={() => setNav({})}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--rsc-primary)] hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid="building-back"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Gebäude wählen
          </button>
        )}
        <header className="mb-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{building.label}</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{building.floors.length} Etagen · {building.floors.reduce((s, f) => s + f.rooms.length, 0)} Räume</p>
        </header>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {building.floors.map((f) => (
            <TileButton
              key={f.key}
              label={f.label}
              subLabel={`${f.rooms.length} Räume`}
              count={f.rooms.length}
              onCount={countOnEntities(f.rooms)}
              onClick={() => setNav({ building: nav.building, floor: f.key })}
              Icon={Layers}
              testId={`floor-tile-${f.key}`}
            />
          ))}
        </div>
      </div>
    );
  }

  // Level 0: Gebäude-Auswahl
  return (
    <div className="space-y-4">
      <header className="mb-2">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Räume</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">Waehle ein Gebaeude oder Bereich.</p>
      </header>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {buildings.map((b) => {
          const roomCount = b.floors.reduce((s, f) => s + f.rooms.length, 0);
          const onCount = countOnEntities(b.floors.flatMap((f) => f.rooms));
          return (
            <TileButton
              key={b.key}
              label={b.label}
              subLabel={`${b.floors.length} ${b.floors.length === 1 ? 'Etage' : 'Etagen'} · ${roomCount} Räume`}
              count={roomCount}
              onCount={onCount}
              onClick={() => setNav({ building: b.key })}
              Icon={Building2}
              testId={`building-tile-${b.key}`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Favoriten
// ============================================================

function FavoritesView({ tabs, favIds }: { tabs: RoomTab[]; favIds: string[] }): JSX.Element {
  const entityRoomMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tabs) for (const e of t.entities) m.set(e.entity_id, t.areaName);
    return m;
  }, [tabs]);
  const allEntities = useMemo(() => tabs.flatMap((t) => t.entities), [tabs]);
  const favEntities = useMemo(
    () => favIds.map((id) => allEntities.find((e) => e.entity_id === id)).filter((e): e is Entity => !!e),
    [favIds, allEntities],
  );

  if (favEntities.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-gray-300 bg-white/40 p-8 text-center dark:border-gray-700 dark:bg-gray-800/30">
        <Star className="mx-auto mb-3 h-8 w-8 text-amber-400" aria-hidden="true" />
        <h3 className="mb-1 text-lg font-bold text-gray-900 dark:text-white">Noch keine Favoriten</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Öffne einen Raum und tippe das Stern-Symbol an einer Kachel, um sie hier zu sammeln.
        </p>
      </section>
    );
  }

  const groups = groupEntitiesByDomain(favEntities);
  return (
    <div className="space-y-4">
      {groups.map((group) => (<DomainSection key={group.key} group={group} entityRoomMap={entityRoomMap} />))}
    </div>
  );
}

// ============================================================
// Zentralfunktionen: Sammelkacheln, klick → filter-Kategorie
// ============================================================

interface CentralAction {
  key: string;
  label: string;
  hint: (n: number, on: number) => string;
  Icon: typeof Lightbulb;
  domainFilter: string[];
}

const CENTRAL_ACTIONS: CentralAction[] = [
  { key: 'lights', label: 'Alle Lichter', hint: (n, on) => `${on} von ${n} an`, Icon: Lightbulb, domainFilter: ['light'] },
  { key: 'switches', label: 'Alle Schalter', hint: (n, on) => `${on} von ${n} an`, Icon: ToggleLeft, domainFilter: ['switch'] },
  { key: 'covers', label: 'Alle Rolläden', hint: (n) => `${n} Rolläden`, Icon: Blinds, domainFilter: ['cover'] },
  { key: 'climate', label: 'Alle Klima', hint: (n) => `${n} Regler`, Icon: Thermometer, domainFilter: ['climate'] },
  { key: 'sensors', label: 'Alle Sensoren', hint: (n) => `${n} Sensoren`, Icon: Gauge, domainFilter: ['sensor', 'binary_sensor'] },
];

function CentralView({ tabs, onSelect }: { tabs: RoomTab[]; onSelect: (domainKey: string) => void }): JSX.Element {
  const allEntities = useMemo(() => tabs.flatMap((t) => t.entities), [tabs]);
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Sammelansichten über alle Raeume. Klick oeffnet die Kategorie-Ansicht mit Raum-Chip pro Kachel.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CENTRAL_ACTIONS.map((a) => {
          const matched = allEntities.filter((e) => a.domainFilter.includes(domainForEntityId(e.entity_id)));
          const onCount = matched.filter((e) => e.state === 'on').length;
          return (
            <TileButton
              key={a.key}
              label={a.label}
              subLabel={a.hint(matched.length, onCount)}
              count={matched.length}
              onCount={onCount}
              onClick={() => onSelect(a.key)}
              Icon={a.Icon}
              testId={`central-${a.key}`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Kategorien-View mit optionalem Domain-Filter + Raum-Chip
// ============================================================

function CategoriesView({ tabs, filter, onClearFilter, resetKey }: { tabs: RoomTab[]; filter?: string; onClearFilter?: () => void; resetKey?: number }): JSX.Element {
  const entityRoomMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tabs) for (const e of t.entities) m.set(e.entity_id, t.areaName);
    return m;
  }, [tabs]);
  const allEntities = useMemo(() => tabs.flatMap((t) => t.entities), [tabs]);
  const allGroups = useMemo(() => groupEntitiesByDomain(allEntities), [allEntities]);

  // Drill-Down: filter aus Prop (von Zentral) ODER lokal ausgewaehlt (nach Klick auf Tile).
  const [localSelected, setLocalSelected] = useState<string | undefined>(undefined);
  useEffect(() => { setLocalSelected(undefined); }, [resetKey]);
  const activeKey = filter ?? localSelected;

  const activeGroup = useMemo(() => {
    if (!activeKey) return undefined;
    const action = CENTRAL_ACTIONS.find((a) => a.key === activeKey);
    if (action) return allGroups.find((g) => g.entities[0] && action.domainFilter.includes(domainForEntityId(g.entities[0].entity_id)));
    return allGroups.find((g) => g.key === activeKey);
  }, [activeKey, allGroups]);

  if (allGroups.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">Keine Geräte gefunden.</p>;

  // Level 1: eine Kategorie gewaehlt -> Cards mit Raum-Chip
  if (activeGroup) {
    const back = () => { if (filter && onClearFilter) onClearFilter(); else setLocalSelected(undefined); };
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={back}
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[var(--rsc-primary)] hover:bg-gray-100 dark:hover:bg-gray-800"
          data-testid="filter-clear"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Alle Kategorien
        </button>
        <header className="mb-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{activeGroup.label}</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{activeGroup.entities.length} Geräte ueber alle Raeume</p>
        </header>
        <DomainSection group={activeGroup} entityRoomMap={entityRoomMap} />
      </div>
    );
  }

  // Level 0: Tiles pro Domain
  return (
    <div className="space-y-4">
      <header className="mb-2">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Kategorien</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">Waehle eine Gruppe fuer die cross-Room-Ansicht.</p>
      </header>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {allGroups.map((g) => {
          const onCount = g.entities.filter((e) => e.state === 'on').length;
          return (
            <TileButton
              key={g.key}
              label={g.label}
              subLabel={`${g.entities.length} Gerate`}
              count={g.entities.length}
              onCount={onCount}
              onClick={() => setLocalSelected(g.key)}
              Icon={g.Icon}
              testId={`category-tile-${g.key}`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ============================================================

// ============================================================
// Surveillance-View (lazy iframe)
// ============================================================

interface Settings { surveillance?: { url?: string } }

function getSurveillanceUrl(): string | undefined {
  try {
    const s = (window as unknown as { __RSC_DASH_SETTINGS?: Settings }).__RSC_DASH_SETTINGS;
    const url = s?.surveillance?.url;
    if (!url || url === '__SAME_ORIGIN__') return url === '__SAME_ORIGIN__' ? window.location.origin : undefined;
    return url;
  } catch { return undefined; }
}

function SurveillanceView({ mounted }: { mounted: boolean }): JSX.Element {
  const url = getSurveillanceUrl();
  const [fullscreen, setFullscreen] = useState(false);
  const requiresSession = sameOriginSurveillance(url);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);
  if (!url) {
    return (
      <section className="rounded-xl border border-dashed border-gray-300 bg-white/40 p-8 text-center dark:border-gray-700 dark:bg-gray-800/30">
        <Camera className="mx-auto mb-3 h-8 w-8 text-gray-400" aria-hidden="true" />
        <h3 className="mb-1 text-lg font-bold text-gray-900 dark:text-white">Surveillance nicht konfiguriert</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Setze <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">SURVEILLANCE_URL</code> als
          Env-Variable im RSC-Hub-Container. Standard: <code>https://cu-&lt;slug&gt;-cam.rscontrol.space</code>.
        </p>
      </section>
    );
  }
  if (!mounted) return <p className="text-sm text-gray-500">Wird geladen…</p>;
  const wrapperClass = fullscreen
    ? 'fixed inset-0 z-[60] overflow-hidden rounded-none bg-black shadow-2xl'
    : 'relative overflow-hidden rounded-xl shadow-md';
  const wrapperStyle: React.CSSProperties = fullscreen
    ? { height: '100vh' }
    : { height: 'calc(100vh - 220px)' };
  const FSIcon = fullscreen ? Minimize2 : Maximize2;
  const injectIntoFrigate = () => {
    // Same-origin iframe: nur Dark-Mode-Force (Uebergang, bis Frigate per
    // rsc-surveillance#64 standardmaessig dunkel ausliefert — dann auch das hier abbauen).
    // Der fruehere Hub-Button-Patch ist ENTFERNT — der native hub-Button lebt
    // im Frigate-Fork (rsc-surveillance MR !17, siehe rsc-dash#5).
    const doc = iframeRef.current?.contentDocument;
    const win = iframeRef.current?.contentWindow;
    if (!doc || !win) return;
    try {
      win.localStorage.setItem("theme", "dark");
      win.localStorage.setItem("vite-ui-theme", "dark");
    } catch { /* ignore */ }
    const forceDark = () => {
      doc.documentElement.classList.add("dark");
      doc.documentElement.classList.remove("light");
      doc.documentElement.style.setProperty("color-scheme", "dark");
    };
    forceDark();
    const obs = new (win as unknown as { MutationObserver: typeof MutationObserver }).MutationObserver(() => {
      if (!doc.documentElement.classList.contains("dark")) forceDark();
    });
    obs.observe(doc.documentElement, { childList: true, subtree: true });
  };
  const iframeHeight = 'calc(100% - 40px)';
  const content = (
    <div className={wrapperClass} style={wrapperStyle}>
      <div
        className="flex h-10 w-full items-center justify-between border-b border-white/10 bg-[#171717] px-2 text-white"
        data-testid="surveillance-pwabar"
      >
        <div className="flex items-center text-[11px] font-medium text-white/70">
          <span><span className="text-[#D40F16]">RS</span><span className="text-[#F0F0F0]">Control</span></span>
          <span className="ml-1.5 text-[#3F7FE0]">hub</span>
        </div>
        <button
          type="button"
          onClick={() => setFullscreen((v) => !v)}
          aria-label={fullscreen ? 'Vollbild verlassen' : 'Vollbild'}
          data-testid="pwabar-fullscreen"
          className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10"
        >
          <FSIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <iframe
        ref={iframeRef}
        src={url}
        title="RSC Surveillance"
        className="w-full border-0"
        style={{ height: iframeHeight }}
        allow="fullscreen; camera; microphone; autoplay"
        loading="eager"
        data-testid="surveillance-iframe"
        onLoad={injectIntoFrigate}
      />
    </div>
  );
  return requiresSession ? <SurveillanceSessionGate key={url}>{content}</SurveillanceSessionGate> : content;
}

// Landing-View
// ============================================================

export interface LandingViewProps {
  loading?: boolean;
  disconnected?: boolean;
  authInvalid?: boolean;
  onRetry?: () => void;
}

const SECTION_STORAGE_KEY = 'rsc-dash.section';

export function LandingView({ loading, disconnected, authInvalid, onRetry }: LandingViewProps): JSX.Element {
  const { state } = useRegistry();
  const { mode } = useViewMode();
  const isElectrician = mode === 'electrician';
  const labeledFavIds = useMemo(() => state.entities.filter((e) => (e.labels ?? []).includes(HA_LABEL_FAV)).map((e) => e.entity_id), [state.entities]);
  const favorites = useFavorites(labeledFavIds);

  const tabs = useMemo<RoomTab[]>(() => {
    const maps = buildRegistryMaps(state);
    const areas: Area[] = [...state.areas].sort((a, b) => a.name.localeCompare(b.name));
    const result: RoomTab[] = [];
    for (const area of areas) {
      const entities = maps.entitiesByArea.get(area.area_id) ?? [];
      const filtered = isElectrician ? entities : entities.filter((e) => !isCustomerHidden(e) || (e.labels || []).includes('rsc-fav'));
      if (filtered.length === 0 && !isElectrician) continue;
      let floorInfo: FloorInfo;
      const haFloor: Floor | undefined = area.floor_id ? maps.floorById.get(area.floor_id) : undefined;
      if (haFloor) {
        floorInfo = { key: `ha-${haFloor.floor_id}`, label: haFloor.name, order: haFloor.level ?? 10 };
      } else {
        floorInfo = extractFloorFromName(area.name);
      }
      result.push({
        areaId: area.area_id,
        areaName: cleanAreaName(area.name),
        raw: area.name,
        entities: filtered,
        building: extractBuilding(area.name),
        floor: floorInfo,
      });
    }
    return result;
  }, [state, isElectrician]);

  const [roomsResetKey, setRoomsResetKey] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [surveillanceMounted, setSurveillanceMounted] = useState(false);
  const [section, setSectionRaw] = useState<string>(() => {
    try { return localStorage.getItem(SECTION_STORAGE_KEY) ?? 'favorites'; } catch { return 'favorites'; }
  });
  const setSection = (next: string) => {
    if (next === "surveillance") setSurveillanceMounted(true);
    if (next === section) {
      // Tab-Klick auf bereits aktiver Sektion -> Reset (zurueck zu Level 1)
      setRoomsResetKey((k) => k + 1);
      setCategoryFilter(undefined);
    } else {
      setSectionRaw(next);
      setRoomsResetKey((k) => k + 1);
      setCategoryFilter(undefined);
    }
  };
  useEffect(() => { try { localStorage.setItem(SECTION_STORAGE_KEY, section); } catch { /* ignore */ } }, [section]);


  if (loading) return <main className="p-4"><EmptyDashboardState reason="connecting" /></main>;
  if (authInvalid) return <main className="p-4"><EmptyDashboardState reason="auth-invalid" onRetry={onRetry} /></main>;
  if (disconnected) return <main className="p-4"><EmptyDashboardState reason="disconnected" onRetry={onRetry} /></main>;
  if (tabs.length === 0) return <main className="p-4"><EmptyDashboardState reason="no-rooms" /></main>;

  return (
    <main className="rsc-landing-view min-h-[calc(100vh-73px)] bg-gray-50 p-4 text-gray-900 dark:bg-gray-900 dark:text-white" data-testid="landing-view">
      <HeroBar tabs={tabs} />
      <Tabs value={section} onValueChange={setSection} className="w-full">
        <div className="sticky top-[64px] z-30 -mx-4 mb-4 border-b border-gray-200/60 bg-white/80 px-4 py-2 backdrop-blur-md dark:border-gray-700/40 dark:bg-gray-900/80">
          <TabsList variant="line" className="flex w-full flex-wrap justify-start gap-1 bg-transparent p-0">
            <TabsTrigger value="favorites" className="px-3 py-2" data-testid="section-favorites" onClick={() => setSection("favorites")}>
              <Star className="h-4 w-4" aria-hidden="true" /> Favoriten
              <span className="ml-1 text-[10px] opacity-60">{favorites.ids.length}</span>
            </TabsTrigger>
            <TabsTrigger value="rooms" className="px-3 py-2" data-testid="section-rooms" onClick={() => setSection("rooms")}>
              <LayoutGrid className="h-4 w-4" aria-hidden="true" /> Räume
              <span className="ml-1 text-[10px] opacity-60">{tabs.length}</span>
            </TabsTrigger>
            <TabsTrigger value="central" className="px-3 py-2" data-testid="section-central" onClick={() => setSection("central")}>
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Zentral
            </TabsTrigger>
            <TabsTrigger value="surveillance" className="px-3 py-2" data-testid="section-surveillance" onClick={() => setSection("surveillance")}>
              <Camera className="h-4 w-4" aria-hidden="true" /> Surveillance
            </TabsTrigger>
            <TabsTrigger value="categories" className="px-3 py-2" data-testid="section-categories" onClick={() => setSection("categories")}>
              <List className="h-4 w-4" aria-hidden="true" /> Kategorien
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="favorites" className="mt-0">
          <FavoritesView tabs={tabs} favIds={favorites.ids} />
        </TabsContent>
        <TabsContent value="rooms" className="mt-0">
          <RoomsView tabs={tabs} resetKey={roomsResetKey} />
        </TabsContent>
        <TabsContent value="central" className="mt-0">
          <CentralView tabs={tabs} onSelect={(key) => { setCategoryFilter(key); setSection('categories'); }} />
        </TabsContent>
        <TabsContent value="surveillance" className="mt-0">
          <SurveillanceView mounted={surveillanceMounted} />
        </TabsContent>
        <TabsContent value="categories" className="mt-0">
          <CategoriesView tabs={tabs} filter={categoryFilter} onClearFilter={() => setCategoryFilter(undefined)} resetKey={roomsResetKey} />
        </TabsContent>
      </Tabs>
    </main>
  );
}

export const RoomTabsView = LandingView;
export type RoomTabsViewProps = LandingViewProps;
